import React from 'react';
import { useTranslation } from 'react-i18next';
import { unescapeHTML } from 'underscore.string';
import { NavLink } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@cargomatic/ui';
import { LifeBuoy, LogOut, User } from 'lucide-react';
import CargomaticLogo from 'img/cargomatic-logo-dark.png';
import SessionStore from 'stores/SessionStore';
import classNames from 'classnames';
import Navbar from '../v2/navs/Navbar';
import UserActionCreators from '../../actions/UserActionCreators';
import { Avatar } from '../modules/user/Avatar';
import LanguageSelector from './language-selector';

const SUPPORT_MAILTO = 'mailto:support@cargomatic.com';

const navLinkClass =
  'group inline-flex h-10 items-center justify-center rounded-md px-3 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-700 hover:text-white focus:bg-gray-700 focus:text-white focus:outline-none disabled:pointer-events-none disabled:opacity-50';

const navLinkActiveClass = 'bg-gray-900 text-white';

const dropdownItemClass =
  'gap-2 text-gray-900 no-underline hover:no-underline focus:bg-gray-100 focus:text-gray-900 focus:outline-none focus-visible:outline-none';

function logout() {
  UserActionCreators.logout();
}

export default function ConsigneeNavigation() {
  const consignee = SessionStore.getUser();
  const { t } = useTranslation();

  function renderMobileNav(onClose) {
    return [
      <NavLink
        className="nav-item"
        to="/tracking"
        onClick={onClose}
        key="mobile-tracking"
      >
        <span>{t('TRACKING')}</span>
      </NavLink>,
      <NavLink
        className="nav-item"
        to="/appointments"
        onClick={onClose}
        key="appointments-tracking"
      >
        <span>{t('APPOINTMENTS')}</span>
      </NavLink>,
      <NavLink
        className="nav-item"
        to={`/consignee/${consignee?._id}`}
        onClick={onClose}
        key="mobile-profiles"
      >
        <span>{t('MY_PROFILE')}</span>
      </NavLink>,
      <a
        href={SUPPORT_MAILTO}
        className="nav-item"
        onClick={onClose}
        key="mobile-contact-support"
      >
        <span>{t('CONTACT_SUPPORT')}</span>
      </a>,
      <div
        key="language-selector-mobile"
        className={classNames('nav-item', {
          hidden: LANGUAGE_SELECTION_DISABLED,
        })}
      >
        <LanguageSelector mobile />
      </div>,
      <a href="#" className="nav-item" onClick={logout} key="mobile-signout">
        {t('SIGN_OUT')}
      </a>,
    ];
  }
  function renderRightNav() {
    const name = SessionStore.getUser().name;
    const userId = SessionStore.getUser()._id;

    const profileButton = (
      <button
        type="button"
        title={unescapeHTML(name)}
        className="flex-shrink-0 flex h-8 w-8 items-center justify-center text-sm bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
        aria-haspopup="true"
        aria-label={unescapeHTML(name)}
      >
        <Avatar
          key={userId}
          userId={userId}
          altName={unescapeHTML(name)}
          className="w-8 h-8 rounded-full object-cover"
        />
      </button>
    );

    return [
      <div
        key="language-selector"
        className={classNames(
          'flex items-center justify-center nav-item mr-2',
          { hidden: LANGUAGE_SELECTION_DISABLED },
        )}
      >
        <LanguageSelector />
      </div>,
      <DropdownMenu key="main-dropdown">
        <DropdownMenuTrigger asChild>{profileButton}</DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem asChild className={dropdownItemClass}>
            <NavLink to={`/consignee/${consignee?._id}`}>
              <User className="h-4 w-4" />
              <span>{t('MY_PROFILE')}</span>
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className={dropdownItemClass}>
            <a href={SUPPORT_MAILTO}>
              <LifeBuoy className="h-4 w-4" />
              <span>{t('CONTACT_SUPPORT')}</span>
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem className={dropdownItemClass} onClick={logout}>
            <LogOut className="h-4 w-4" />
            <span>{t('SIGN_OUT')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>,
    ];
  }
  const brandLogo = (
    <NavLink to="/">
      <img alt="Cargomatic" src={CargomaticLogo} style={{ height: 25 }} />
    </NavLink>
  );

  return (
    <Navbar
      logo={brandLogo}
      renderRightNav={renderRightNav}
      renderMobileNav={renderMobileNav}
    >
      <NavigationMenu
        key="primary-nav"
        className="min-w-0 max-w-none flex-1 justify-start"
      >
        <NavigationMenuList className="min-w-0 flex-nowrap items-center justify-start gap-1 space-x-0 overflow-x-auto scrollbar-thin">
          <NavigationMenuItem className="flex-shrink-0">
            <NavigationMenuLink asChild>
              <NavLink
                to="/tracking"
                className={navLinkClass}
                activeClassName={navLinkActiveClass}
              >
                {t('TRACKING')}
              </NavLink>
            </NavigationMenuLink>
          </NavigationMenuItem>
          <NavigationMenuItem className="flex-shrink-0">
            <NavigationMenuLink asChild>
              <NavLink
                to="/appointments"
                className={navLinkClass}
                activeClassName={navLinkActiveClass}
              >
                {t('APPOINTMENTS')}
              </NavLink>
            </NavigationMenuLink>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
    </Navbar>
  );
}
